/**
 * 呼叫「經 BFF 代理的後端 API」。
 *
 * 前端沒有 access token —— token 全程留在 auth-service 的 session 裡，瀏覽器只有
 * HttpOnly 的 session cookie。因此各 app 不直接呼叫領域服務（api.codeincold.com），
 * 而是打同一個 BFF，由它換上 token 再轉發。
 *
 * 這也是為什麼這支工具放在 shared-auth 而不是各 app 自己寫：「怎麼帶身分」是認證的
 * 一部分，散在各 app 就會出現有的帶 cookie、有的漏 CSRF 的分歧。
 */

import axios, { AxiosError, type AxiosRequestConfig } from 'axios';

import { apiUrl } from './auth-config';
import { readCsrfToken } from './csrf';
import { notifySessionExpired } from './session-expiry';

/** 不改變狀態的方法不需要 CSRF token（Spring 的 CsrfFilter 也只檢查其餘方法）。 */
const SAFE_METHODS = new Set(['get', 'head', 'options', 'trace']);

/**
 * 呼叫失敗時拋出，帶上 HTTP 狀態供呼叫端分辨。
 *
 * 401 特別值得分辨：那代表 session 過期，畫面該回到登入而不是顯示「載入失敗」。
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** session 已失效，需要重新登入 */
  get isUnauthenticated(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * 共用的 axios instance。
 *
 * 帶身分的三件事——session cookie、CSRF token、401 轉登入——都收斂成這裡的
 * interceptor，各呼叫點只描述「要打哪支 API」。這正是選 axios 而非 `fetch` 的
 * 理由：否則每個呼叫點都得自己組 headers、判 `response.ok`、再解析錯誤 body。
 *
 * ⚠️ 不設 `baseURL`：BFF 的位址由 `auth-config` 的各個 URL 函式統一組裝，
 *    位址知識留在那一處，這裡只負責「怎麼送」。
 */
export const httpClient = axios.create({
  /* session cookie 在另一個子網域，不帶 credentials 就等於匿名請求 */
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

/**
 * 寫入操作補上 CSRF header。
 *
 * ⚠️ 不用 axios 內建的 `xsrfCookieName`：那會連安全方法也一併帶上，而這裡刻意
 *    與後端的檢查範圍對齊——需要 token 的就只有會改變狀態的方法。
 *
 * ⚠️ 這個 cookie 的 domain 為 `codeincold.com`，本機開發（localhost）讀不到，
 *    故本機的寫入操作會被 BFF 擋下。與登出相同的限制，見 shared-auth README。
 */
httpClient.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (SAFE_METHODS.has(method)) return config;

  const csrf = readCsrfToken();
  if (csrf) config.headers.set('X-XSRF-TOKEN', csrf);
  return config;
});

/**
 * 把非 2xx 回應轉成 `ApiError`，並在 session 失效時通知登入層。
 *
 * 通知的理由：否則呼叫端只能顯示「載入失敗＋重試」，而重試會再拿一次同樣的錯誤，
 * 使用者除了自己重新整理頁面別無出路。
 *
 * 401 一律視為失效；403 則只在安全方法上視為失效：寫入被回 403 多半是 CSRF token
 * 不合（例如本機開發讀不到那個 cookie），把它當成過期會讓開發者每次寫入都被踢回
 * 登入頁。而 GET 不經 CSRF 檢查，它的 403 就只可能是身分問題。
 *
 * ⚠️ 連不上（沒有 response）時原封不動往外拋：那是網路或 CORS 問題而非後端的答覆，
 *    包成 `ApiError` 會讓 `@shared/query` 的重試判準誤以為「後端說不行」而放棄重試。
 */
httpClient.interceptors.response.use(undefined, (error: unknown) => {
  if (!(error instanceof AxiosError) || !error.response) throw error;

  const { status } = error.response;
  const method = (error.config?.method ?? 'get').toLowerCase();
  const isSessionExpired =
    status === 401 || (status === 403 && SAFE_METHODS.has(method));

  if (isSessionExpired) {
    notifySessionExpired();
  }
  throw new ApiError(status, errorMessage(error.response.data, status));
});

/**
 * 對 BFF 發出 JSON 請求。
 *
 * @param path   BFF 上的路徑，例如 `/api/diet/entries`
 * @param config 一般的 axios 選項；請求內容傳 `data`（物件即可，不必自行序列化）
 * @throws ApiError 非 2xx 回應
 */
export async function apiFetch<T>(
  path: string,
  config: AxiosRequestConfig = {},
): Promise<T> {
  const response = await httpClient.request<T>({ ...config, url: apiUrl(path) });

  /* 204 或空回應（例如 DELETE）沒有 JSON 可回，axios 會給空字串 */
  return (response.data === '' ? undefined : response.data) as T;
}

/** 取後端的錯誤訊息；格式不符時退回狀態碼描述，不讓解析失敗蓋掉原始錯誤。 */
function errorMessage(data: unknown, status: number): string {
  /* 後端不一定回 JSON（例如 502 由反向代理產生），非物件一律走預設訊息 */
  if (data && typeof data === 'object' && 'message' in data) {
    const { message } = data as { message?: unknown };
    if (typeof message === 'string' && message) return message;
  }
  return `請求失敗（HTTP ${status}）`;
}
